import { motion } from "framer-motion";
import { type ReactNode } from "react";

import { cn } from "#app/utils/misc.tsx";
import { Icon } from "../ui/icon";

/**
 * The empty-chat backdrop from the prototype (`app/(main)/chat/page.tsx`): a
 * soft radial wash over a 48px graph-paper grid. It replaces the old
 * `chat-gradient-bg.svg` bitmap, which could not follow the theme.
 *
 * The indigo and slate here are literal, matching the prototype. Mapping them
 * onto `--primary` looked wrong: this app's primary is near-black, so the wash
 * came out grey instead of the lavender the design calls for.
 *
 * Rendered behind the hero and composer, so it must stay non-interactive.
 */

export function ChatLandingBackdrop({
  className,
  wash = "900px 500px",
}: {
  className?: string;
  /**
   * Size of the radial wash, as a CSS `<length>{2}` pair.
   *
   * The default is the prototype's full-viewport figure. A short, wide panel —
   * the marketplace hero — needs a flatter ellipse: at 500px tall the gradient
   * is clipped to its own middle band, so the tint stops reading as a wash and
   * looks like a flat grey fill.
   */
  wash?: string;
}) {
  return (
    // <div
    //   aria-hidden
    //   className={cn("pointer-events-none absolute inset-0 z-0", className)}
    //   style={{
    //     backgroundImage: `
    //       radial-gradient(
    //         ellipse ${wash} at 50% 50%,
    //         rgba(99, 102, 241, 0.18) 0%,
    //         rgba(99, 102, 241, 0.08) 25%,
    //         transparent 75%
    //       ),
    //       linear-gradient(rgba(203, 213, 225, 0.25) 1px, transparent 1px),
    //       linear-gradient(90deg, rgba(203, 213, 225, 0.25) 1px, transparent 1px)
    //     `,
    //     backgroundSize: "100% 100%, 48px 48px, 48px 48px",
    //     backgroundPosition: "0 0, -1px -1px, -1px -1px",
    //   }}
    // />

    <div
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-0 z-0",
        "[--grid-line:rgba(203,213,225,0.15)] dark:[--grid-line:rgba(203,213,225,0.02)]",
        className,
      )}
      style={{
        backgroundImage: `
      radial-gradient(
        ellipse ${wash} at 50% 50%,
        rgba(99, 102, 241, 0.18) 0%,
        rgba(99, 102, 241, 0.2) 25%,
        transparent 80%
      ),
      linear-gradient(var(--grid-line) 1px, transparent 1px),
      linear-gradient(90deg, var(--grid-line) 1px, transparent 1px)
    `,
        backgroundSize: "100% 100%, 48px 48px, 48px 48px",
        backgroundPosition: "0 0, -1px -1px, -1px -1px",
      }}
    />
  );
}

/**
 * The headline shown above the composer before the first message. A single line,
 * like the prototype's: the "Hi <name>," greeting that used to sit above it now
 * lives in the page header (see chat-header/index.tsx), which said "New chat"
 * there and so read as the less useful of the two labels.
 *
 * Centred over the composer rather than left-aligned — flush-left put the text
 * against the card's left edge while the suggestion row below stayed centred, so
 * the three stacked elements never shared an axis.
 */
export function ChatLandingHero({
  headline,
  children,
  showLogo = true,
}: {
  headline: ReactNode;
  children?: ReactNode;
  showLogo?: boolean;
}) {
  return (
    <div className="relative z-10 flex w-full items-center justify-center">
      {/* `max-w-3xl` is not arbitrary: it must match the width <ChatComposer>
          is given in chat-main, or the headline's left edge hangs outside the
          composer card (it used to be `md:w-3/4 md:max-w-4xl`, 64px wider). */}
      {/* `mb-10` matches the prototype's gap between the headline and the card;
          `mb-7` left them looking crowded against the composer's tall shadow. */}
      <div className="mb-10 w-full max-w-3xl text-center">
        {/* Block, not `inline-block`: an inline-block only honours the parent's
            `text-center` as a whole, so a headline that wraps to two lines had
            its own lines left-aligned inside the centred box. */}
        {showLogo && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.4,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="sm:hidden"
          >
            <div className="flex items-center justify-center mb-5">
              {/* <img src="/img/supervity-logo.svg" className="mx-auto max-w-10" /> */}
              <Icon name="supervity" className="h-10 w-10 shrink-0" />
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{
            duration: 0.6,
            ease: [0.22, 1, 0.36, 1],
          }}
        >
          <h1 className="text-[28px] font-light tracking-[-0.02em] text-balance text-foreground xl:text-4xl">
            {headline}
          </h1>
        </motion.div>
        {children}
      </div>
    </div>
  );
}
